import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Reveal, SectionTitle } from "../components/shared";
import { InquiryForm } from "../components/InquiryForm";
import { Button } from "../components/ui/button";
import { Palette, Sprout, ChefHat, ArrowRight, Sparkles } from "lucide-react";

const SOCCER = "https://images.pexels.com/photos/10885468/pexels-photo-10885468.jpeg?auto=compress&cs=tinysrgb&w=1200";

const icons = { atelier: Palette, garden: Sprout, cooking: ChefHat };

export default function Programs() {
  const { t } = useI18n();
  const [programs, setPrograms] = useState([]);
  useEffect(() => { api.get("/programs").then(({ data }) => setPrograms(data)).catch(() => {}); }, []);

  const byAge = programs.filter((p) => !p.featured);
  const signature = programs.filter((p) => p.featured);

  return (
    <div className="pt-28">
      <section className="max-w-7xl mx-auto px-6 py-12">
        <Reveal>
          <div className="text-coral font-semibold tracking-widest uppercase text-xs mb-3">Programs</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy tracking-tight max-w-3xl">Programs for every age & every curiosity</h1>
          <p className="mt-5 text-lg text-navy/60 max-w-2xl leading-relaxed">From our gentle infant nest to project-based preschool — plus three signature studios woven into every child's day.</p>
        </Reveal>
      </section>

      {/* By age */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <Reveal><SectionTitle title={t.sections.programsTitle} /></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {byAge.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div className="bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] h-full flex flex-col" data-testid={`program-age-${p.slug}`}>
                <img src={p.image} alt={p.name} className="h-52 w-full object-cover" />
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs font-semibold text-coral uppercase tracking-wider">{p.age}</div>
                  <h3 className="font-heading text-2xl font-bold text-navy mt-1">{p.name}</h3>
                  <p className="text-navy/60 mt-3 leading-relaxed flex-1">{p.summary}</p>
                  <InquiryForm type="enrollment" defaultProgram={p.name} trigger={<Button variant="ghost" className="mt-4 justify-start px-0 text-coral hover:text-coral-dark hover:bg-transparent" data-testid={`program-inquire-${p.slug}`}>Inquire about {p.name} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Soccer highlight */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden bg-navy text-white grid lg:grid-cols-2 shadow-xl">
            <div className="relative min-h-[260px]">
              <img src={SOCCER} alt="Children playing soccer" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="p-10 sm:p-12 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mustard text-navy text-xs font-semibold uppercase tracking-widest w-fit"><Sparkles className="h-3.5 w-3.5" /> New · Coming Soon</span>
              <h2 className="font-heading text-3xl font-bold mt-4">Weekly Soccer Program</h2>
              <p className="mt-4 text-cream/80 text-lg leading-relaxed">A dedicated soccer coach will visit Ainalem once a week, guiding our little ones through playful games that build coordination, teamwork, and confidence.</p>
              <InquiryForm type="enrollment" defaultProgram="Weekly Soccer Program" title="Soccer Program Interest"
                trigger={<Button className="mt-6 rounded-full bg-coral hover:bg-coral-dark text-white w-fit" data-testid="programs-soccer-btn">Register Interest</Button>} />
            </div>
          </div>
        </Reveal>
      </section>

      {/* Signature detailed */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionTitle eyebrow="What makes us special" title={t.sections.signatureTitle} subtitle={t.sections.signatureSub} /></Reveal>
          <div className="space-y-16">
            {signature.map((p, i) => {
              const Icon = icons[p.slug] || Palette;
              const reverse = i % 2 === 1;
              return (
                <Reveal key={p.id}>
                  <div className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
                    <img src={p.image} alt={p.name} className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover lg:[direction:ltr]" />
                    <div className="lg:[direction:ltr]">
                      <div className="h-12 w-12 rounded-2xl bg-mustard flex items-center justify-center text-navy mb-4"><Icon className="h-6 w-6" /></div>
                      <h3 className="font-heading text-3xl font-bold text-navy">{p.name}</h3>
                      <p className="text-lg text-navy/70 mt-4 leading-relaxed">{p.detail || p.summary}</p>
                      <InquiryForm type="tour" defaultProgram={p.name} trigger={<Button className="mt-6 rounded-full bg-navy hover:bg-navy-dark text-white" data-testid={`signature-tour-${p.slug}`}>Book a tour to see it</Button>} />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
